import { Component } from 'react';
import type { CSSProperties, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Title } from '@/shared/ui';

interface AuthenticatedErrorBoundaryProps {
  children: ReactNode;
}

interface AuthenticatedErrorBoundaryState {
  hasError: boolean;
}

const FALLBACK_CONTAINER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--text-primary, #1a1a1a)',
};

const FALLBACK_TITLE_STYLE: CSSProperties = {
  fontSize: '1.25rem',
  marginBottom: 'var(--space-2)',
};

/**
 * Branded fallback shown when the authenticated error boundary catches a
 * render error. Uses the translation hook so its copy is localized; kept as a
 * function component because class error boundaries cannot consume hooks.
 *
 * @returns The localized fallback alert node.
 */
function AuthenticatedErrorFallback(): ReactNode {
  const { t } = useTranslation('common');
  return (
    <div style={FALLBACK_CONTAINER_STYLE} role="alert">
      <div>
        <Title order={1} style={FALLBACK_TITLE_STYLE}>
          {t('sharedComponents.authenticatedErrorBoundary.title')}
        </Title>
        <Text style={{ opacity: 0.7 }}>
          {t('sharedComponents.authenticatedErrorBoundary.description')}
        </Text>
      </div>
    </div>
  );
}

/**
 * Error boundary for the authenticated application shell.
 *
 * Catches render errors thrown anywhere inside the authenticated route tree
 * and renders a clean, branded fallback instead of crashing the whole app to
 * a blank page. Errors are reported to the browser console in development
 * only; no error details are sent off-device.
 */
export class AuthenticatedErrorBoundary extends Component<
  AuthenticatedErrorBoundaryProps,
  AuthenticatedErrorBoundaryState
> {
  state: AuthenticatedErrorBoundaryState = { hasError: false };

  /**
   * Update state so the next render shows the fallback UI.
   *
   * @returns The next error boundary state.
   */
  static getDerivedStateFromError(): AuthenticatedErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * Log the caught error to the console in development environments only.
   *
   * @param error - The error that was thrown.
   * @param info - React component stack information.
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('AuthenticatedErrorBoundary caught an error', error, info);
    }
  }

  /**
   * Render the children, or a fallback message when an error was caught.
   *
   * @returns The rendered node.
   */
  render(): ReactNode {
    if (this.state.hasError) {
      return <AuthenticatedErrorFallback />;
    }

    return this.props.children;
  }
}
